import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getTechnicalReports,
  getFeedbackReports,
  updateTechnicalReport,
  updateFeedbackReport,
  deleteTechnicalReport,
  deleteFeedbackReport,
  getUserById,
  getJiraConfigStatus,
  saveJiraConfig,
  createJiraIssue,
  getJiraIssue,
  getJiraIssueTypes,
  type JiraConfigStatus,
  type JiraIssueDetails,
  type JiraIssueType,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import { TestingManager } from './TestingManager';
import { ReportScreenshot } from './ReportScreenshot';
import type {
  TechnicalReport,
  FeedbackReport,
  ReportStatus,
  FirestoreUser,
} from '../../types';
import { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';

type ReportTab = 'technical' | 'feedback' | 'testing';
type StatusFilter = 'all' | ReportStatus;

function formatDate(timestamp: Timestamp | undefined): string {
  if (!timestamp) return '-';
  const date = timestamp.toDate();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusBadgeVariant(status?: ReportStatus): 'warning' | 'info' | 'success' {
  switch (status) {
    case 'in_progress':
      return 'info';
    case 'closed':
      return 'success';
    default:
      return 'warning';
  }
}

function statusLabel(status?: ReportStatus): string {
  switch (status) {
    case 'in_progress':
      return 'In Progress';
    case 'closed':
      return 'Closed';
    default:
      return 'Open';
  }
}

export function ReportsManager() {
  const [activeTab, setActiveTab] = useState<ReportTab>('technical');
  const [technicalReports, setTechnicalReports] = useState<TechnicalReport[]>([]);
  const [feedbackReports, setFeedbackReports] = useState<FeedbackReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Detail modal state
  const [selectedTechnical, setSelectedTechnical] = useState<TechnicalReport | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackReport | null>(null);

  // Jira config state
  const [jiraConfig, setJiraConfig] = useState<JiraConfigStatus | null>(null);
  const [showJiraSettings, setShowJiraSettings] = useState(false);

  useEffect(() => {
    loadReports();
    loadJiraConfig();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [tech, feedback] = await Promise.all([
        getTechnicalReports(),
        getFeedbackReports(),
      ]);
      setTechnicalReports(tech);
      setFeedbackReports(feedback);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadJiraConfig = async () => {
    try {
      const config = await getJiraConfigStatus();
      setJiraConfig(config);
    } catch {
      // Jira not configured yet — that's fine
      setJiraConfig({ configured: false });
    }
  };

  const filteredTechnical = useMemo(() => {
    return technicalReports.filter((r) => {
      const effectiveStatus = r.status || 'open';
      if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          r.userMessage?.toLowerCase().includes(term) ||
          r.deviceModel?.toLowerCase().includes(term) ||
          r.currentRoute?.toLowerCase().includes(term) ||
          r.userId?.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [technicalReports, statusFilter, searchTerm]);

  const filteredFeedback = useMemo(() => {
    return feedbackReports.filter((r) => {
      const effectiveStatus = r.status || 'open';
      if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          r.message?.toLowerCase().includes(term) ||
          r.currentRoute?.toLowerCase().includes(term) ||
          r.userId?.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [feedbackReports, statusFilter, searchTerm]);

  const handleTechnicalUpdate = (updated: TechnicalReport) => {
    setTechnicalReports((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r))
    );
    setSelectedTechnical(updated);
  };

  const handleTechnicalDelete = (id: string) => {
    setTechnicalReports((prev) => prev.filter((r) => r.id !== id));
    setSelectedTechnical(null);
  };

  const handleFeedbackUpdate = (updated: FeedbackReport) => {
    setFeedbackReports((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r))
    );
    setSelectedFeedback(updated);
  };

  const handleFeedbackDelete = (id: string) => {
    setFeedbackReports((prev) => prev.filter((r) => r.id !== id));
    setSelectedFeedback(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  const techOpenCount = technicalReports.filter((r) => !r.status || r.status === 'open').length;
  const feedbackOpenCount = feedbackReports.filter((r) => !r.status || r.status === 'open').length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Reports</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage technical reports and user feedback submitted via shake-to-report.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <button
          onClick={loadReports}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          title="Refresh reports"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
        <button
          onClick={() => setShowJiraSettings(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          title="Jira Settings"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Jira
          {jiraConfig?.configured ? (
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          ) : (
            <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
          )}
        </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('technical')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'technical'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Technical Reports
          {techOpenCount > 0 && (
            <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs">
              {techOpenCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'feedback'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Feedback
          {feedbackOpenCount > 0 && (
            <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs">
              {feedbackOpenCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('testing')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'testing'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Testing
        </button>
      </div>

      {/* Filters (not shown on Testing tab) */}
      {activeTab !== 'testing' && (
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
          <div className="relative w-full sm:flex-1">
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      )}

      {/* Technical Reports Table */}
      {activeTab === 'technical' && (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jira</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTechnical.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedTechnical(report)}>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(report.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs truncate max-w-[120px]">
                        {report.userId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                        {report.userMessage || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {report.deviceModel}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {report.jiraTicket ? (
                          <JiraTicketBadge ticketKey={report.jiraTicket} baseUrl={jiraConfig?.baseUrl} />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusBadgeVariant(report.status)}>
                          {statusLabel(report.status)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedTechnical(report); }}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredTechnical.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || statusFilter !== 'all' ? 'No reports match your filters' : 'No technical reports yet'}
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">{filteredTechnical.length} of {technicalReports.length} reports</p>
        </>
      )}

      {/* Feedback Reports Table */}
      {activeTab === 'feedback' && (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jira</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredFeedback.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedFeedback(report)}>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(report.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs truncate max-w-[120px]">
                        {report.userId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                        {report.message || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {report.currentRoute}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {report.jiraTicket ? (
                          <JiraTicketBadge ticketKey={report.jiraTicket} baseUrl={jiraConfig?.baseUrl} />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusBadgeVariant(report.status)}>
                          {statusLabel(report.status)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedFeedback(report); }}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredFeedback.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || statusFilter !== 'all' ? 'No reports match your filters' : 'No feedback reports yet'}
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">{filteredFeedback.length} of {feedbackReports.length} reports</p>
        </>
      )}

      {/* Testing Tab */}
      {activeTab === 'testing' && (
        <TestingManager jiraConfig={jiraConfig} />
      )}

      {/* Technical Report Detail Modal */}
      {selectedTechnical && (
        <TechnicalReportDetail
          report={selectedTechnical}
          onClose={() => setSelectedTechnical(null)}
          onUpdate={handleTechnicalUpdate}
          onDelete={handleTechnicalDelete}
          jiraConfig={jiraConfig}
        />
      )}

      {/* Feedback Report Detail Modal */}
      {selectedFeedback && (
        <FeedbackReportDetail
          report={selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          onUpdate={handleFeedbackUpdate}
          onDelete={handleFeedbackDelete}
          jiraConfig={jiraConfig}
        />
      )}

      {/* Jira Settings Modal */}
      {showJiraSettings && (
        <JiraSettingsModal
          currentConfig={jiraConfig}
          onClose={() => setShowJiraSettings(false)}
          onSaved={(config) => {
            setJiraConfig(config);
            setShowJiraSettings(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Jira Ticket Badge (inline in grid)
// ============================================================================

function JiraTicketBadge({ ticketKey, baseUrl }: { ticketKey: string; baseUrl?: string | null }) {
  const url = baseUrl ? `${baseUrl}/browse/${ticketKey}` : null;

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
      >
        {ticketKey}
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
      {ticketKey}
    </span>
  );
}

// ============================================================================
// Jira Section in Detail Modals
// ============================================================================

interface JiraSectionProps {
  reportId: string;
  reportCollection: 'technicalReports' | 'feedbackReports';
  jiraTicket: string;
  setJiraTicket: (value: string) => void;
  jiraConfig: JiraConfigStatus | null;
  summary: string;
  description: string;
}

function JiraSection({ reportId, reportCollection, jiraTicket, setJiraTicket, jiraConfig, summary, description }: JiraSectionProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [jiraDetails, setJiraDetails] = useState<JiraIssueDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [issueTypes, setIssueTypes] = useState<JiraIssueType[]>([]);
  const [selectedIssueType, setSelectedIssueType] = useState<string>('');
  const [issueTypesLoading, setIssueTypesLoading] = useState(false);

  const loadJiraDetails = useCallback(async (key: string) => {
    if (!key) return;
    setDetailsLoading(true);
    try {
      const details = await getJiraIssue(key);
      setJiraDetails(details);
    } catch {
      // Could not fetch details — that's okay
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Load issue types when Jira is configured and no ticket exists yet
  useEffect(() => {
    if (jiraConfig?.configured && !jiraTicket && issueTypes.length === 0) {
      setIssueTypesLoading(true);
      getJiraIssueTypes()
        .then((types) => {
          setIssueTypes(types);
          // Default to Bug for technical, Task for feedback, or first available
          const defaultType = reportCollection === 'technicalReports' ? 'Bug' : 'Task';
          const matched = types.find((t) => t.name.toLowerCase() === defaultType.toLowerCase());
          setSelectedIssueType(matched?.name || types[0]?.name || '');
        })
        .catch(() => {
          // Could not fetch types
        })
        .finally(() => {
          setIssueTypesLoading(false);
        });
    }
  }, [jiraConfig?.configured, jiraTicket, issueTypes.length, reportCollection]);

  useEffect(() => {
    if (jiraTicket && jiraConfig?.configured) {
      loadJiraDetails(jiraTicket);
    }
  }, [jiraTicket, jiraConfig?.configured, loadJiraDetails]);

  const handleCreateTicket = async () => {
    setIsCreating(true);
    try {
      const result = await createJiraIssue({
        reportId,
        reportCollection,
        summary,
        description,
        issueType: selectedIssueType || undefined,
      });
      setJiraTicket(result.key);
      toast.success(`Created ${result.key}`);
      loadJiraDetails(result.key);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to create Jira ticket';
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  };

  // If Jira is not configured, show the manual input
  if (!jiraConfig?.configured) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Jira Ticket</label>
        <input
          type="text"
          value={jiraTicket}
          onChange={(e) => setJiraTicket(e.target.value)}
          placeholder="e.g., WK-123 (configure Jira in settings for auto-create)"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
      </div>
    );
  }

  // Jira is configured
  if (jiraTicket) {
    // Show linked ticket with details
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Jira Ticket</label>
        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <a
                href={`${jiraConfig.baseUrl}/browse/${jiraTicket}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                {jiraTicket}
              </a>
              {detailsLoading ? (
                <span className="text-xs text-gray-400">Loading...</span>
              ) : jiraDetails ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                  {jiraDetails.status}
                </span>
              ) : null}
            </div>
            <button
              onClick={() => loadJiraDetails(jiraTicket)}
              className="text-xs text-gray-500 hover:text-gray-700"
              title="Refresh status"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          {jiraDetails && (
            <div className="mt-2 text-xs text-gray-500 space-y-1">
              <p className="truncate">{jiraDetails.summary}</p>
              <div className="flex gap-3">
                {jiraDetails.assignee && <span>Assignee: {jiraDetails.assignee}</span>}
                {jiraDetails.priority && <span>Priority: {jiraDetails.priority}</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No ticket yet — show create button with issue type selector
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Jira Ticket</label>
      <div className="flex gap-2 items-center">
        {issueTypesLoading ? (
          <span className="text-xs text-gray-400">Loading issue types...</span>
        ) : issueTypes.length > 0 ? (
          <>
            <select
              value={selectedIssueType}
              onChange={(e) => setSelectedIssueType(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              disabled={isCreating}
            >
              {issueTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={handleCreateTicket}
              loading={isCreating}
              disabled={isCreating || !selectedIssueType}
            >
              Create
            </Button>
            <span className="text-xs text-gray-500">
              in {jiraConfig.projectKey}
            </span>
          </>
        ) : (
          <>
            <Button
              size="sm"
              onClick={handleCreateTicket}
              loading={isCreating}
              disabled={isCreating}
            >
              Create Jira Ticket
            </Button>
            <span className="text-xs text-gray-500">
              in {jiraConfig.projectKey}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Technical Report Detail
// ============================================================================

interface TechnicalReportDetailProps {
  report: TechnicalReport;
  onClose: () => void;
  onUpdate: (report: TechnicalReport) => void;
  onDelete: (id: string) => void;
  jiraConfig: JiraConfigStatus | null;
}

function TechnicalReportDetail({ report, onClose, onUpdate, onDelete, jiraConfig }: TechnicalReportDetailProps) {
  const [user, setUser] = useState<FirestoreUser | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [status, setStatus] = useState<ReportStatus>(report.status || 'open');
  const [jiraTicket, setJiraTicket] = useState(report.jiraTicket || '');
  const [adminNotes, setAdminNotes] = useState(report.adminNotes || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    async function loadUser() {
      try {
        const u = await getUserById(report.userId);
        setUser(u);
      } catch {
        // User might not exist
      } finally {
        setUserLoading(false);
      }
    }
    loadUser();
  }, [report.userId]);

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      const updates: Partial<TechnicalReport> = {
        status,
        jiraTicket: jiraTicket || undefined,
        adminNotes: adminNotes || undefined,
        ...(status === 'closed' && !report.closedAt ? { closedAt: Timestamp.now() } : {}),
      };
      await updateTechnicalReport(report.id, updates);
      onUpdate({ ...report, ...updates, closedAt: updates.closedAt || report.closedAt });
      toast.success('Report updated');
    } catch {
      toast.error('Failed to update report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      await deleteTechnicalReport(report.id);
      toast.success('Report deleted');
      onDelete(report.id);
    } catch {
      toast.error('Failed to delete report');
    } finally {
      setIsProcessing(false);
    }
  };

  const jiraSummary = `[Technical] ${(report.userMessage || 'Technical report').replace(/[\r\n]+/g, ' ').slice(0, 100)} (${report.platform || 'unknown'})`;
  const jiraDescription = [
    `User Message: ${report.userMessage || 'None'}`,
    `Device: ${report.deviceModel} (${report.platform} ${report.osVersion})`,
    `App Version: ${report.appVersion} (${report.buildNumber})`,
    `Route: ${report.currentRoute}`,
    `Network: ${report.networkStatus}`,
    `User ID: ${report.userId}`,
    `Reported: ${formatDate(report.timestamp)}`,
  ].join('\n');

  return (
    <>
      <Modal isOpen onClose={onClose} title="Technical Report" size="lg">
        <div className="space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Submitted By</h4>
            {userLoading ? (
              <div className="animate-pulse h-4 bg-gray-200 rounded w-48"></div>
            ) : user ? (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-900">{user.displayName || 'No name'}</span>
                {user.email && (
                  <a href={`mailto:${user.email}`} className="text-sm text-brand-600 hover:underline">
                    {user.email}
                  </a>
                )}
                <span className="text-xs text-gray-500">Plan: {user.plan} | UID: {report.userId}</span>
              </div>
            ) : (
              <span className="text-sm text-gray-500 font-mono">{report.userId}</span>
            )}
          </div>

          {/* Message */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">User Message</h4>
            <p className="text-sm text-gray-900 bg-white border border-gray-200 rounded-lg p-3">
              {report.userMessage || 'No message provided'}
            </p>
          </div>

          {/* Screenshot — renders nothing when none was attached. */}
          <ReportScreenshot
            screenshotPath={report.screenshotPath}
            reportLabel={`Technical report ${report.id}`}
          />

          {/* Device Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Device Information</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <InfoField label="Device" value={report.deviceModel} />
              <InfoField label="Platform" value={report.platform} />
              <InfoField label="OS Version" value={report.osVersion} />
              <InfoField label="App Version" value={report.appVersion} />
              <InfoField label="Build" value={report.buildNumber} />
              <InfoField label="Network" value={report.networkStatus} />
              <InfoField label="Route" value={report.currentRoute} />
              <InfoField label="Submitted" value={formatDate(report.timestamp)} />
            </div>
          </div>

          {/* Admin Controls */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Admin</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ReportStatus)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <JiraSection
                reportId={report.id}
                reportCollection="technicalReports"
                jiraTicket={jiraTicket}
                setJiraTicket={setJiraTicket}
                jiraConfig={jiraConfig}
                summary={jiraSummary}
                description={jiraDescription}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes about this report..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isProcessing}
            >
              Delete
            </Button>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} disabled={isProcessing}>Cancel</Button>
              <Button onClick={handleSave} loading={isProcessing}>Save</Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete Report" size="sm">
        <div className="space-y-4">
          <p className="text-gray-600">Are you sure you want to delete this technical report? This cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={isProcessing}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={isProcessing}>Delete</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ============================================================================
// Feedback Report Detail
// ============================================================================

interface FeedbackReportDetailProps {
  report: FeedbackReport;
  onClose: () => void;
  onUpdate: (report: FeedbackReport) => void;
  onDelete: (id: string) => void;
  jiraConfig: JiraConfigStatus | null;
}

function FeedbackReportDetail({ report, onClose, onUpdate, onDelete, jiraConfig }: FeedbackReportDetailProps) {
  const [user, setUser] = useState<FirestoreUser | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [status, setStatus] = useState<ReportStatus>(report.status || 'open');
  const [jiraTicket, setJiraTicket] = useState(report.jiraTicket || '');
  const [adminNotes, setAdminNotes] = useState(report.adminNotes || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    async function loadUser() {
      try {
        const u = await getUserById(report.userId);
        setUser(u);
      } catch {
        // User might not exist
      } finally {
        setUserLoading(false);
      }
    }
    loadUser();
  }, [report.userId]);

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      const updates: Partial<FeedbackReport> = {
        status,
        jiraTicket: jiraTicket || undefined,
        adminNotes: adminNotes || undefined,
        ...(status === 'closed' && !report.closedAt ? { closedAt: Timestamp.now() } : {}),
      };
      await updateFeedbackReport(report.id, updates);
      onUpdate({ ...report, ...updates, closedAt: updates.closedAt || report.closedAt });
      toast.success('Report updated');
    } catch {
      toast.error('Failed to update report');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      await deleteFeedbackReport(report.id);
      toast.success('Report deleted');
      onDelete(report.id);
    } catch {
      toast.error('Failed to delete report');
    } finally {
      setIsProcessing(false);
    }
  };

  const jiraSummary = `[Feedback] ${(report.message || 'User feedback').replace(/[\r\n]+/g, ' ').slice(0, 100)}`;
  const jiraDescription = [
    `Feedback: ${report.message || 'None'}`,
    `App Version: ${report.appVersion}`,
    `Route: ${report.currentRoute}`,
    `User ID: ${report.userId}`,
    `Reported: ${formatDate(report.timestamp)}`,
  ].join('\n');

  return (
    <>
      <Modal isOpen onClose={onClose} title="Feedback Report" size="lg">
        <div className="space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Submitted By</h4>
            {userLoading ? (
              <div className="animate-pulse h-4 bg-gray-200 rounded w-48"></div>
            ) : user ? (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-900">{user.displayName || 'No name'}</span>
                {user.email && (
                  <a href={`mailto:${user.email}`} className="text-sm text-brand-600 hover:underline">
                    {user.email}
                  </a>
                )}
                <span className="text-xs text-gray-500">Plan: {user.plan} | UID: {report.userId}</span>
              </div>
            ) : (
              <span className="text-sm text-gray-500 font-mono">{report.userId}</span>
            )}
          </div>

          {/* Message */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">Feedback Message</h4>
            <p className="text-sm text-gray-900 bg-white border border-gray-200 rounded-lg p-3">
              {report.message || 'No message provided'}
            </p>
          </div>

          {/* Screenshot — renders nothing when none was attached. */}
          <ReportScreenshot
            screenshotPath={report.screenshotPath}
            reportLabel={`Feedback report ${report.id}`}
          />

          {/* Report Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Report Information</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <InfoField label="App Version" value={report.appVersion} />
              <InfoField label="Route" value={report.currentRoute} />
              <InfoField label="Submitted" value={formatDate(report.timestamp)} />
            </div>
          </div>

          {/* Admin Controls */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Admin</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ReportStatus)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <JiraSection
                reportId={report.id}
                reportCollection="feedbackReports"
                jiraTicket={jiraTicket}
                setJiraTicket={setJiraTicket}
                jiraConfig={jiraConfig}
                summary={jiraSummary}
                description={jiraDescription}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes about this feedback..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isProcessing}
            >
              Delete
            </Button>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} disabled={isProcessing}>Cancel</Button>
              <Button onClick={handleSave} loading={isProcessing}>Save</Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete Report" size="sm">
        <div className="space-y-4">
          <p className="text-gray-600">Are you sure you want to delete this feedback report? This cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={isProcessing}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={isProcessing}>Delete</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ============================================================================
// Jira Settings Modal
// ============================================================================

interface JiraSettingsModalProps {
  currentConfig: JiraConfigStatus | null;
  onClose: () => void;
  onSaved: (config: JiraConfigStatus) => void;
}

function JiraSettingsModal({ currentConfig, onClose, onSaved }: JiraSettingsModalProps) {
  const [email, setEmail] = useState(currentConfig?.email || '');
  const [apiToken, setApiToken] = useState('');
  const [baseUrl, setBaseUrl] = useState(currentConfig?.baseUrl || 'https://jwsadler.atlassian.net');
  const [projectKey, setProjectKey] = useState(currentConfig?.projectKey || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!email || !apiToken || !baseUrl || !projectKey) {
      toast.error('All fields are required');
      return;
    }

    setIsSaving(true);
    try {
      await saveJiraConfig({ email, apiToken, baseUrl, projectKey });
      toast.success('Jira configuration saved and verified');
      onSaved({
        configured: true,
        baseUrl,
        projectKey: projectKey.toUpperCase(),
        email,
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to save Jira configuration';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Jira Integration Settings" size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Connect to your Atlassian Jira instance to create and track tickets directly from reports.
          Credentials are stored securely and only accessible via Cloud Functions.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Atlassian Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            API Token
            {currentConfig?.configured && (
              <span className="text-xs text-gray-400 ml-2">(leave blank to keep existing)</span>
            )}
          </label>
          <input
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            placeholder={currentConfig?.configured ? '••••••••' : 'Paste your Atlassian API token'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            Generate at id.atlassian.com &gt; Security &gt; API tokens
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jira Base URL</label>
          <input
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://yourteam.atlassian.net"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project Key</label>
          <input
            type="text"
            value={projectKey}
            onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
            placeholder="e.g., WK"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            The project key prefix for tickets (e.g., WK creates WK-1, WK-2, etc.)
          </p>
        </div>

        {currentConfig?.configured && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-700">
              Currently connected to <strong>{currentConfig.baseUrl}</strong> (project: {currentConfig.projectKey})
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} loading={isSaving}>
            {currentConfig?.configured ? 'Update Configuration' : 'Save & Test Connection'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Shared Components
// ============================================================================

function InfoField({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="bg-white border border-gray-200 rounded px-3 py-2">
      <span className="block text-xs text-gray-500">{label}</span>
      <span className="block text-sm text-gray-900 truncate">{value || '-'}</span>
    </div>
  );
}
